import { useMemo } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  dateFormatter,
  formatWeight,
  RIR_AT_LOAD_MIN_WEEKS,
  rirAtSameLoad,
  type Point,
} from "@heraion/shared";

import { BarChart } from "../charts/BarChart";
import { ChartCappedNote } from "../charts/ChartCappedNote";
import { ChartHead, chartTitleStyle } from "../charts/ChartHead";
import { useChartReadout } from "../charts/pointSelection";
import { useExerciseNameBySlug } from "../Logger/exerciseName";
import { useDateLocale } from "../primitives/DateFormatContext";
import { weightUnitLabels } from "../weightUnitLabels";
import { PanelTitleRow } from "./InfoPill";
import { NoDataCard, StaleHint } from "./panelCards";
import { makeTierOneStyles } from "./panelStyles";
import { panelDays, panelRange } from "./progressWindow";
import type { ProgressDeps } from "./types";
import { useSessionResultsFromDeps } from "./useSessionResults";
import { useSetsInWindow } from "./useWindowSets";

// #124: the reps in reserve at the SAME exercise, weight and reps, week by
// week. It reads the raw sets of the interval it is given (`deps.range`,
// 8 weeks on Progress; the report will pass its period). A drop in the bars
// means the same work costs more: fatigue, measured, not advised on.
export function RirAtLoadPanel(deps: ProgressDeps) {
  const { theme, units, apiFetch, range, weekStartDay } = deps;
  const { t, i18n } = useTranslation();
  const unitLabels = useMemo(() => weightUnitLabels(t), [t]);
  const { catalog, error: catalogError } = useSessionResultsFromDeps(deps);
  const getName = useExerciseNameBySlug(catalog);
  const win = useMemo(() => panelRange(range), [range]);
  const { sets, error: setsError, capped } = useSetsInWindow(apiFetch, win);
  const styles = useMemo(() => makeTierOneStyles(theme), [theme]);
  const dateLocale = useDateLocale();
  const series = useMemo(
    () => (sets ? rirAtSameLoad(sets, win, weekStartDay) : null),
    [sets, win, weekStartDay],
  );
  const points = useMemo<Point[]>(() => {
    const day = dateFormatter(dateLocale, { day: "numeric", month: "short" });
    return (series?.weeks ?? []).map((w) => ({
      x: w.weekStartMs,
      y: w.meanRir,
      label: t("mobile.progress.rirAtLoad.weekSets", {
        date: day.format(new Date(w.weekStartMs)),
        count: w.sets,
        defaultValue_one: "week of {{date}}, {{count}} set",
        defaultValue_other: "week of {{date}}, average of {{count}} sets",
      }),
    }));
  }, [series, dateLocale, t]);
  const tick = useMemo(() => {
    const short = dateFormatter(dateLocale, { day: "numeric", month: "numeric" });
    return (p: Point) => short.format(new Date(p.x));
  }, [dateLocale]);
  const rirFormat = useMemo(() => {
    const f = new Intl.NumberFormat(i18n.language, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    return (n: number) => f.format(n);
  }, [i18n.language]);
  const readoutFmt = (n: number) =>
    t("mobile.progress.rirAtLoad.readout", {
      rir: rirFormat(n),
      defaultValue: "{{rir}} RIR",
    });
  const { selection, readout, readoutProps } = useChartReadout(
    points,
    readoutFmt,
    false,
  );

  const weeks = Math.max(1, Math.round(panelDays(win) / 7));
  const title = t("mobile.progress.rirAtLoad.title", {
    defaultValue: "Reserve at the same load",
  });
  const help = t("mobile.progress.rirAtLoad.help", {
    weeks,
    defaultValue:
      "Same exercise, same weight, same reps: how many reps you had left, week by week over {{weeks}} weeks. If the bars fall, the same work is costing more and fatigue is building up. Only effort-tagged sets count; a week without one has no bar, it is not a zero.",
  });
  const period = t("mobile.progress.rirAtLoad.period", {
    weeks,
    defaultValue: "{{weeks}} wk",
  });

  const error = catalogError || setsError;
  if (sets === null || !catalog) {
    return <NoDataCard theme={theme} titleKey="rirAtLoad" error={error || null} />;
  }
  if (series === null) {
    return (
      <View style={styles.card}>
        <PanelTitleRow theme={theme} title={title} body={help} period={period} />
        {error ? <StaleHint theme={theme} /> : null}
        <Text style={styles.empty}>
          {t("mobile.progress.rirAtLoad.empty", {
            count: RIR_AT_LOAD_MIN_WEEKS,
            defaultValue_one:
              "Needs the same exercise, weight and reps, with effort tagged, in {{count}} different week",
            defaultValue_other:
              "Needs the same exercise, weight and reps, with effort tagged, in {{count}} different weeks",
          })}
        </Text>
        {capped ? <ChartCappedNote theme={theme} /> : null}
      </View>
    );
  }
  // Weeks between the first and the last bar that have no data: told in a
  // line, never drawn as a zero bar.
  const first = series.weeks[0]!.weekStartMs;
  const last = series.weeks[series.weeks.length - 1]!.weekStartMs;
  const skipped = Math.round((last - first) / (7 * 86_400_000)) + 1 - series.weeks.length;
  const name = getName(series.slug, series.snapshot);
  const setLabel = t("mobile.progress.rirAtLoad.setLabel", {
    name,
    reps: series.reps,
    load: formatWeight(series.weightKg, units, i18n.language, unitLabels),
    defaultValue: "{{name}}: {{reps}} × {{load}}",
  });
  return (
    <View style={styles.card}>
      <PanelTitleRow theme={theme} title={title} body={help} period={period} />
      {error ? <StaleHint theme={theme} /> : null}
      {readout === undefined ? null : (
        <View style={styles.trendRow}>
          {/* The exercise name can be long: it wraps on its own line, the
              head keeps a short title that never needs to. */}
          <Text style={chartTitleStyle(theme)}>{setLabel}</Text>
          <ChartHead
            theme={theme}
            title={t("mobile.progress.rirAtLoad.rirTitle", {
              defaultValue: "Reps in reserve",
            })}
            readout={readout}
            readoutProps={readoutProps}
          />
          <BarChart
            theme={theme}
            points={points}
            valueFormat={rirFormat}
            readoutFormat={readoutFmt}
            selection={selection}
            tick={tick}
          />
        </View>
      )}
      {skipped > 0 ? (
        <Text style={styles.empty}>
          {t("mobile.progress.rirAtLoad.skipped", {
            count: skipped,
            defaultValue_one: "{{count}} week without data",
            defaultValue_other: "{{count}} weeks without data",
          })}
        </Text>
      ) : null}
      {capped ? <ChartCappedNote theme={theme} /> : null}
    </View>
  );
}
